/**
 * Risk scoring add-ons only exist for Diligent templates: the engine scores
 * against the Diligent rubric, which a sponsor's custom template never has.
 * The request form offers the add-ons whenever at least one selected template
 * can be scored; a custom template in the same request simply gets none, and
 * every risk surface stays off a VQQ that never asked for one.
 */

/** A template option is custom when the catalog marks it non-global. */
export function isCustomTemplateOption(template) {
  return template?.global === false;
}

/** True when any selected template is a custom one. */
export function hasCustomTemplateSelected(templates) {
  return (Array.isArray(templates) ? templates : []).some(isCustomTemplateOption);
}

/** True when every selected template is a custom one, so nothing in the request can be scored. */
export function onlyCustomTemplatesSelected(templates) {
  const list = Array.isArray(templates) ? templates : [];
  return list.length > 0 && list.every(isCustomTemplateOption);
}

/**
 * What the request form may show and send for the add-ons. When nothing in
 * the selection can be scored the block hides and both flags go off, whatever
 * the sponsor's quota allows; otherwise the quota gates decide, and the
 * per-template flags keep a custom template in the same request at none.
 */
export function resolveRiskAddOnRequest(gates = {}) {
  const { templates, canRequestAutoScore = false, canRequestSmeScore = false, enableAutoScore = false, enableSmeScore = false } = gates;
  const forTemplate = (template) => riskAddOnFlagsForTemplate(template, gates);
  if (onlyCustomTemplatesSelected(templates)) {
    return { showAddOns: false, enableAutoScore: false, enableSmeScore: false, forTemplate };
  }
  return {
    showAddOns: !!(canRequestAutoScore || canRequestSmeScore),
    enableAutoScore: !!(canRequestAutoScore && enableAutoScore),
    enableSmeScore: !!(canRequestSmeScore && enableSmeScore),
    forTemplate,
  };
}

/**
 * The two flags a request payload carries for one selected template; the
 * request-wide gates still apply, only the template changes.
 */
export function riskAddOnFlagsForTemplate(template, gates = {}) {
  const custom = isCustomTemplateOption(template);
  return {
    enable_auto_score: !custom && !!(gates.canRequestAutoScore && gates.enableAutoScore),
    enable_sme_score: !custom && !!(gates.canRequestSmeScore && gates.enableSmeScore),
  };
}

/**
 * Whether a VQQ asked for risk scoring at all. Custom VQQs never did, and a
 * Diligent VQQ only did when one of the add-ons was selected at request time.
 */
export function vqqRiskScoringRequested(details) {
  if (!details) return false;
  if (details.is_global_template === false) return false;
  return details.enable_auto_score === true || details.enable_sme_score === true;
}
